import type { ComponentProps, FC } from 'react';
import { Aligner } from '@alfa-bank/site-aligner';
import React, { useState } from 'react';
import cn from 'classnames';
import { useNamedContainersWithChildren } from '@alfa-bank/site-named-container/utils';
import { AnalyticsSpy } from '@alfa-bank/site-dc-form-utils/components/analytics-spy';
import { FormBirthday as Birthday } from '@alfa-bank/site-form-birthday';
import { DcPhoneInput as Phone } from '@alfa-bank/site-dc-phone-input';
import { Grid } from '@alfa-bank/site-grid-universal';
import { Email } from '@alfa-bank/site-form-email';
import { FormEmbossing as Embossing } from '@alfa-bank/site-form-embossing';
import { NamedContainer } from '@alfa-bank/site-named-container';
import { InformationCircleMIcon } from '@alfalab/icons-glyph/InformationCircleMIcon';
import type { IStyler } from '@alfa-bank/site-util-create-widget/src/models';
import { Field } from '@alfa-bank/site-form-utils/components/form-field';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import { FormDraftFirstStep } from '@alfa-bank/site-form-draft-first-step';

import type { IAdditionalProductMultipleProps } from './components/additional-product-multiple/models';
import type { TClientValue, TDcFormShort, TResidentValue } from './models';
import { convertCardsConfig, getCardParams } from './utils/helpers';
import { MobileIdDisabledInput } from './components/mobile-id-disabled-input';
import { Promocode } from './components/promocode-field';

// eslint-disable-next-line sonarjs/cognitive-complexity,complexity
export const DcFormShort: FC<TDcFormShort> = ({
    AdditionalProductMultiple,
    BonusCard,
    Cashback,
    DcDraft,
    FioField,
    Form,
    NotResident,
    Plate,
    ProductDesign,
    Progress,
    RadioTags,
    Submit,
    Tags,
    Text,
    actionPath,
    additionalProducts,
    analytics,
    birthday,
    bonusCard,
    buttonText,
    cards,
    carousel,
    cashback,
    cashbackPackages,
    children,
    client,
    clientPlate,
    dataTestId,
    draft,
    email,
    embossing,
    fio,
    flexibleAlfaCard,
    formAction = {},
    formHash,
    initCard = 0,
    initPaySystem = 0,
    isMobile,
    isNewDesign,
    isPhoneFirst,
    mobileIdDisabled,
    notResident,
    onSubmit,
    oneFieldFio = true,
    paySystem,
    phone,
    product = 'dc-short',
    productDesign,
    progress,
    progressType = 'general',
    promocode,
    queryNamePhone,
    refId = '',
    rejectPhone940 = false,
    resident,
    securityText,
    showFormOuterWrapper = true,
    styleConfig,
    styles,
    subHeading,
    typeSubmitButton = 'securityWarrantyBlock',
    withDraftFirstStep = false,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const productConfig = flexibleAlfaCard ? styleConfig.flexibleDesign : styleConfig.default;

    const [isResident, setIsResident] = useState<TResidentValue>('resident');
    const [isClient, setIsClient] = useState<TClientValue>('notClient');
    const [isOneFieldFio, setIsOneFieldFio] = useState(oneFieldFio);

    const { additionalField, additionalFieldTop, addonsTop, productsSlot, sopdContent, spy } =
        useNamedContainersWithChildren(children);
    const cardParams = getCardParams(cards, paySystem?.[initPaySystem].value.toString(), initCard);

    const phoneProps: ComponentProps<typeof Phone> = {
        name: 'phone',
        queryName: queryNamePhone,
        rejectPhone940,
        settings: phone,
        styler: { marginBottom: 24 },
    };

    const birthdayProps: ComponentProps<typeof Birthday> = {
        ageLimit: birthday?.ageLimit,
        name: 'passportBirthDate',
        settings: birthday,
        styler: { marginBottom: 24 },
    };

    const showCashbackCarousel =
        isClient === 'notClient' && cashbackPackages && AdditionalProductMultiple;

    const showAdditionalProductsCarousel =
        additionalProducts && AdditionalProductMultiple && additionalProducts.products.length > 1;
    const gridColumnWidth = isMobile ? ['auto', 0, 'auto'] : [271, 16, 271];
    const getChipsGridStyler = (): IStyler => {
        if (flexibleAlfaCard) {
            return { marginBottom: isMobile ? 24 : 40, marginTop: isMobile ? 24 : 40 };
        } else if (client && isResident === 'resident') {
            return { marginTop: isMobile ? 16 : 32, marginBottom: 32 };
        } else if (resident) {
            return { marginBottom: 24 };
        } else {
            return {};
        }
    };

    const getFlexibleStyler = (flexibleDesignStyler: IStyler, defaultStyler: IStyler) => {
        return flexibleAlfaCard ? flexibleDesignStyler : defaultStyler;
    };

    return (
        <DcDraft {...draft} cards={convertCardsConfig(cards)}>
            <>
                <Form
                    actionPath={actionPath}
                    analytics={{
                        submit: analytics?.submit?.success,
                        submitOnFail: analytics?.submit?.fail,
                    }}
                    businessLoggingSource="FormSenderDc"
                    dataTestId={dataTestId}
                    formAction={{ ...formAction, refId, ...cardParams }}
                    formHash={formHash}
                    onSubmit={onSubmit}
                    product={product}
                    showFormOuterWrapper={showFormOuterWrapper}
                >
                    {addonsTop}
                    {analytics && <AnalyticsSpy analytics={analytics} resident={isResident} />}
                    {progress && !flexibleAlfaCard && (
                        <Progress {...progress} type={progressType} />
                    )}
                    {withDraftFirstStep && (
                        <FormDraftFirstStep
                            mapFields={{ gender: 'sex', birthday: 'birthDate' }}
                            sentryCaptureException={{
                                packageName: 'dc-form-short',
                                teamName: 'dc',
                            }}
                            setIsOneFieldFio={setIsOneFieldFio}
                        />
                    )}
                    <Grid
                        className={
                            flexibleAlfaCard
                                ? cn(styles?.flexibleDesignRadioTags, styles?.external)
                                : undefined
                        }
                        colAutoWidth={flexibleAlfaCard}
                        collapse={flexibleAlfaCard}
                        columnsWidth={flexibleAlfaCard ? gridColumnWidth : undefined}
                        justify={flexibleAlfaCard ? 'start' : 'center'}
                        styler={getChipsGridStyler()}
                        vertical={isMobile}
                    >
                        {resident && !flexibleAlfaCard && (
                            <RadioTags
                                caption={resident.caption}
                                name="resident"
                                onSelect={({ value }) => setIsResident(value as TResidentValue)}
                                selected={isResident}
                                size="xs"
                                tags={resident.tags}
                                titleClassName={cn(styles?.tagCaption, styles?.external)}
                            />
                        )}
                        {resident && flexibleAlfaCard && Tags && (
                            <Tags
                                caption={resident.caption}
                                name="resident"
                                onClick={value => setIsResident(value as TResidentValue)}
                                selected={isResident}
                                tags={resident?.tags}
                            />
                        )}
                        {client && flexibleAlfaCard && <></>}
                        {client && isResident === 'resident' && (
                            <Field<TClientValue>
                                initialValue="notClient"
                                name="isClient"
                                render={({ input, meta }) => {
                                    return flexibleAlfaCard && Tags ? (
                                        <Tags
                                            caption={client?.caption}
                                            name="client"
                                            onClick={value => {
                                                actions.onChange(
                                                    input,
                                                    meta
                                                )(value as TClientValue);
                                                setIsClient(value as TClientValue);
                                            }}
                                            selected={input.value}
                                            styler={{ marginTop: isMobile ? 24 : 0 }}
                                            tags={client?.tags}
                                        />
                                    ) : (
                                        <RadioTags
                                            caption={client.caption}
                                            name="client"
                                            onSelect={({ value }) => {
                                                actions.onChange(
                                                    input,
                                                    meta
                                                )(value as TClientValue);
                                                setIsClient(value as TClientValue);
                                            }}
                                            selected={input.value}
                                            size="xs"
                                            styler={{ marginTop: isMobile ? 24 : 0 }}
                                            tags={client.tags}
                                            titleClassName={cn(
                                                styles?.tagCaption,
                                                styles?.external
                                            )}
                                        />
                                    );
                                }}
                            />
                        )}
                    </Grid>
                    <div
                        style={{
                            display: isResident === 'resident' ? 'block' : 'none',
                        }}
                    >
                        {showCashbackCarousel && (
                            <AdditionalProductMultiple
                                {...cashbackPackages}
                                card={
                                    cashbackPackages?.card || {
                                        width: isMobile ? '152px' : '178px',
                                        height: isMobile ? '260px' : '262px',
                                    }
                                }
                                carousel={carousel}
                                imagePosition="top"
                                isFlexibleAlfaCard={flexibleAlfaCard}
                                styler={getFlexibleStyler(
                                    {
                                        marginTop: isMobile ? 24 : 40,
                                        marginBottom: isMobile ? 24 : 40,
                                    },
                                    {
                                        marginTop: 32,
                                        marginBottom: 32,
                                    }
                                )}
                            />
                        )}
                        {isClient === 'client' && (
                            <Plate
                                border={false}
                                className={
                                    flexibleAlfaCard
                                        ? cn(styles?.flexibleDesignClientPlate, styles?.external)
                                        : cn(styles?.clientPlate, styles?.external)
                                }
                                shadow={false}
                                styler={getFlexibleStyler(
                                    {
                                        marginBottom: isMobile ? 24 : 40,
                                    },
                                    { marginBottom: 32 }
                                )}
                                view={flexibleAlfaCard ? 'custom' : 'common'}
                            >
                                <NamedContainer name="leftAddons">
                                    <InformationCircleMIcon
                                        className={
                                            flexibleAlfaCard
                                                ? undefined
                                                : cn(styles?.iconHint, styles?.external)
                                        }
                                        color={flexibleAlfaCard ? '#0E0E0E' : undefined}
                                    />
                                </NamedContainer>
                                {clientPlate}
                            </Plate>
                        )}
                        {flexibleAlfaCard && (
                            <div
                                style={{
                                    width: isMobile ? 'auto' : '558px',
                                    paddingLeft: isMobile ? '24px' : '97px',
                                    paddingRight: isMobile ? '24px' : '97px',
                                }}
                            >
                                {isClient === 'notClient' &&
                                    (cashback?.cashbackCategories ||
                                        cashback?.cashbackPackages) && (
                                        <Cashback
                                            cashbackCategories={cashback?.cashbackCategories}
                                            cashbackPackages={cashback?.cashbackPackages}
                                            styler={
                                                isMobile
                                                    ? { marginBottom: 24, marginTop: 24 }
                                                    : { marginBottom: 40, marginTop: 40 }
                                            }
                                        />
                                    )}
                                {additionalField}
                            </div>
                        )}
                        {showAdditionalProductsCarousel && (
                            <AdditionalProductMultiple
                                imagePosition="bottom"
                                isFlexibleAlfaCard={flexibleAlfaCard}
                                styler={getFlexibleStyler(
                                    {
                                        marginBottom: isMobile ? 24 : 40,
                                    },
                                    {
                                        marginBottom: isMobile ? 24 : 32,
                                    }
                                )}
                                {...(additionalProducts as IAdditionalProductMultipleProps)}
                                card={
                                    (additionalProducts as IAdditionalProductMultipleProps)
                                        ?.card || {
                                        width: isMobile ? '152px' : '178px',
                                        height: isMobile ? '144px' : '164px',
                                    }
                                }
                                carousel={carousel}
                            />
                        )}
                        {productDesign && (
                            <ProductDesign
                                {...productDesign}
                                analytics={analytics?.productDesign}
                                card={productConfig}
                                carousel={carousel}
                                flexibleAlfaCard={flexibleAlfaCard}
                                styler={{ marginBottom: 24 }}
                            />
                        )}

                        <div
                            className={
                                flexibleAlfaCard
                                    ? cn(styles?.flexibleDesignWrapper, styles?.external)
                                    : undefined
                            }
                        >
                            {flexibleAlfaCard && subHeading && (
                                <Text
                                    styler={{ margin: 0, paddingBottom: 24 }}
                                    view={isMobile ? 'xsmall' : 'small'}
                                >
                                    {subHeading}
                                </Text>
                            )}
                            {progress && flexibleAlfaCard && (
                                <Progress {...progress} type={progressType} />
                            )}
                            {additionalFieldTop}
                            {isPhoneFirst && <Phone {...phoneProps} />}
                            <FioField
                                {...fio}
                                analytics={{
                                    expandToThreeFields: {},
                                    hasMiddleName: analytics?.hasMiddleName?.click,
                                }}
                                fioCaption={fio.caption}
                                fioPlaceholder={fio.placeholder}
                                genderTagsSize="xs"
                                name={{
                                    lastName: 'lastName',
                                    firstName: 'firstName',
                                    middleName: 'middleName',
                                    sex: 'sex',
                                }}
                                oneFieldFio={isOneFieldFio}
                            />
                            {!isPhoneFirst && birthday && <Birthday {...birthdayProps} />}
                            <Grid vertical={isMobile}>
                                {isPhoneFirst ? (
                                    birthday && <Birthday {...birthdayProps} />
                                ) : (
                                    <Phone {...phoneProps} />
                                )}
                                {email && (
                                    <Email
                                        name="email"
                                        optional={email.optional}
                                        settings={email}
                                        styler={{ marginBottom: 24 }}
                                    />
                                )}
                            </Grid>
                            {promocode && (
                                <Promocode
                                    name="promocode"
                                    settings={promocode}
                                    styler={{ marginBottom: 24 }}
                                />
                            )}
                            {embossing && (
                                <Embossing
                                    autoComplete={embossing.autoComplete}
                                    hidden={embossing.hidden}
                                    name="embossingName"
                                    settings={embossing}
                                    styler={{ marginBottom: 24 }}
                                />
                            )}
                            {spy}
                            {bonusCard && <BonusCard {...bonusCard} />}
                            {!flexibleAlfaCard && additionalField}
                            {!isMobile && (
                                <>
                                    <Submit
                                        buttonText={buttonText}
                                        isNewDesign={isNewDesign}
                                        securityText={securityText}
                                        styler={
                                            !isMobile && isNewDesign
                                                ? { marginTop: 8, borderRadius: 24 }
                                                : {
                                                      borderRadius:
                                                          flexibleAlfaCard && !isMobile
                                                              ? 24
                                                              : undefined,
                                                  }
                                        }
                                        type={typeSubmitButton}
                                    />
                                    <Text
                                        fontPreset={'primaryNormal'}
                                        styler={{
                                            marginBottom: isNewDesign || flexibleAlfaCard ? 0 : 16,
                                            marginTop: isNewDesign ? 16 : 24,
                                        }}
                                        tag="div"
                                    >
                                        {sopdContent}
                                    </Text>
                                </>
                            )}
                        </div>
                        {productsSlot}
                    </div>
                    <NotResident
                        className={
                            flexibleAlfaCard
                                ? cn(styles?.flexibleDesignNotResident, styles?.external)
                                : undefined
                        }
                        isMobile={isMobile}
                        isNewDesign={isNewDesign}
                        isResident={isResident}
                        notResident={notResident}
                        showFormOuterWrapper={showFormOuterWrapper}
                    />
                    <MobileIdDisabledInput mobileIdDisabled={mobileIdDisabled} />

                    {isMobile && isResident !== 'notResident' && (
                        <>
                            <Submit
                                buttonText={buttonText}
                                isNewDesign={isNewDesign}
                                securityText={securityText}
                                styler={{
                                    paddingLeft: flexibleAlfaCard ? 24 : undefined,
                                    paddingRight: flexibleAlfaCard ? 24 : undefined,
                                    marginTop: flexibleAlfaCard ? 24 : undefined,
                                }}
                                type={typeSubmitButton}
                            />
                            <Aligner
                                align="center"
                                styler={{
                                    marginTop: 16,
                                    paddingLeft: flexibleAlfaCard ? 24 : undefined,
                                    paddingRight: flexibleAlfaCard ? 24 : undefined,
                                    paddingBottom: flexibleAlfaCard ? 24 : undefined,
                                }}
                            >
                                <Text fontPreset="primarySmall" tag="div">
                                    {sopdContent}
                                </Text>
                            </Aligner>
                        </>
                    )}
                </Form>
            </>
        </DcDraft>
    );
};
